import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Business information state for institutional investor onboarding
 * Stores company details, registration info, and investment preferences
 */
export interface InstitutionalBusinessState {
  company: string | null;
  regNumber: string | null;
  legalStructure: string | null;
  legalStructureOthers: string | null;
  countryOfIncorp: string | null;
  countryOfDomicile: string | null;
  region: string | null;
  city: string | null;
  website: string | null;
  address: string | null;
  postal: string | null;
  phoneNumber: string | null;
  industryGroup: string | null;
  industryDropdownValues: any | null; // TODO: Define proper type
  industry: string | null;
  industryOthers: string | null;
  investmentAmount: number | null;
  investorDeclarationQualified: boolean | null;
  incorporationDate: string | null;
}

const initialState: InstitutionalBusinessState = {
  company: null,
  regNumber: null,
  legalStructure: null,
  legalStructureOthers: null,
  countryOfIncorp: null,
  countryOfDomicile: null,
  region: null,
  city: null,
  website: null,
  address: null,
  phoneNumber: null,
  postal: null,
  industryGroup: null,
  industryDropdownValues: null,
  industry: null,
  industryOthers: null,
  investmentAmount: null,
  investorDeclarationQualified: null,
  incorporationDate: null,
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof InstitutionalBusinessState>(field: K) => {
  return (
    state: InstitutionalBusinessState,
    action: PayloadAction<InstitutionalBusinessState[K]>,
  ) => {
    state[field] = action.payload;
  };
};

const bizSlice = createSlice({
  name: "insitutionalBiz",
  initialState,
  reducers: {
    // Company identification
    setCompanyRedux: createFieldSetter("company"),
    setRegNumberRedux: createFieldSetter("regNumber"),
    setIncorporationDateRedux: createFieldSetter("incorporationDate"),

    // Legal structure
    setLegalStructureRedux: createFieldSetter("legalStructure"),
    setLegalStructureOthersRedux: createFieldSetter("legalStructureOthers"),

    // Location details
    setCountryOfIncorpRedux: createFieldSetter("countryOfIncorp"),
    setCountryOfDomicileRedux: createFieldSetter("countryOfDomicile"),
    setRegionRedux: createFieldSetter("region"),
    setCityRedux: createFieldSetter("city"),
    setAddressRedux: createFieldSetter("address"),
    setPostalCodeRedux: createFieldSetter("postal"),

    // Contact information
    setPhoneNumberRedux: createFieldSetter("phoneNumber"),
    setWebsiteRedux: createFieldSetter("website"),

    // Industry classification
    setIndustryGroupRedux: createFieldSetter("industryGroup"),
    setIndustryDropdownValuesRedux: createFieldSetter("industryDropdownValues"),
    setIndustryRedux: createFieldSetter("industry"),
    setIndustryOthersRedux: createFieldSetter("industryOthers"),

    // Investment details
    setInvestmentAmountRedux: createFieldSetter("investmentAmount"),
    setInvestorDeclarationQualifiedRedux: createFieldSetter("investorDeclarationQualified"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateInstitutionalBizState: (
      state,
      action: PayloadAction<Partial<InstitutionalBusinessState>>,
    ) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetInstitutionalBizState: () => initialState,
  },
});

export const {
  setCompanyRedux,
  setRegNumberRedux,
  setLegalStructureRedux,
  setCountryOfIncorpRedux,
  setCountryOfDomicileRedux,
  setRegionRedux,
  setCityRedux,
  setAddressRedux,
  setPhoneNumberRedux,
  setPostalCodeRedux,
  setWebsiteRedux,
  setLegalStructureOthersRedux,
  setIndustryGroupRedux,
  setIndustryDropdownValuesRedux,
  setIndustryRedux,
  setIndustryOthersRedux,
  setInvestmentAmountRedux,
  setInvestorDeclarationQualifiedRedux,
  setIncorporationDateRedux,
  hydrateInstitutionalBizState,
  resetInstitutionalBizState,
} = bizSlice.actions;

export default bizSlice.reducer;
